import React from 'react';

import { Box, Paper, Skeleton, Typography } from '@mui/material';

import { spacingStyling } from 'src/constants/spacing';
import { useScreenInfo } from 'src/hooks/common';
import { SxProp } from 'src/types/mui';
import { composeSx } from 'src/utils/styles';

const HEADING_FONT_SIZE = '34px';
const SUBHEADING_FONT_SIZE = '30px';

interface PageLayoutProps {
  titleContent: React.ReactNode;
  subTitleContent?: React.ReactNode;
  topRightContent?: React.ReactNode | React.ReactNode[];
  loading?: boolean;
  paperSx?: SxProp;
  containerSx?: SxProp;
  topRightContentSx?: SxProp;
}

export const PageLayout: React.FC<React.PropsWithChildren<PageLayoutProps>> = ({
  titleContent,
  subTitleContent,
  topRightContent,
  loading,
  children,
  paperSx,
  containerSx = {},
  topRightContentSx,
}) => {
  const { isMobile } = useScreenInfo();

  const renderTitleContent = React.useCallback(() => {
    if (isMobile) return null;

    if (loading) {
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <Skeleton variant="circular" width={HEADING_FONT_SIZE} height={HEADING_FONT_SIZE} />
          <Skeleton width={200} height={HEADING_FONT_SIZE} />
        </Box>
      );
    }

    return (
      <Typography
        variant="h2"
        fontWeight="400"
        fontSize={HEADING_FONT_SIZE}
        lineHeight={HEADING_FONT_SIZE}
        data-e2e-element="page-heading"
      >
        {titleContent}
      </Typography>
    );
  }, [isMobile, loading, titleContent]);

  return (
    <Box sx={composeSx(pageContainerStyles, containerSx)}>
      {!isMobile && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          minHeight="40px"
          gap={2}
        >
          {renderTitleContent()}
          <Box sx={topRightContentSx}>{topRightContent}</Box>
        </Box>
      )}

      <Paper sx={composeSx(basePaperStyles, paperSx)} elevation={0}>
        {subTitleContent && (
          <Typography
            variant="h3"
            fontWeight="bold"
            fontSize={SUBHEADING_FONT_SIZE}
            lineHeight={SUBHEADING_FONT_SIZE}
          >
            {subTitleContent}
          </Typography>
        )}

        {children}
      </Paper>
    </Box>
  );
};

const pageContainerStyles: SxProp = theme => ({
  display: 'flex',
  flexFlow: 'column nowrap',
  height: '100%',
  overflow: 'hidden',
  padding: spacingStyling.pageContainer,
  [theme.breakpoints.down('sm')]: {
    padding: 0,
  },
});

const basePaperStyles: SxProp = theme => ({
  mt: 3,
  borderRadius: 2,
  overflow: 'auto',
  boxShadow: '-5px 5px 30px 0 rgba(171, 171, 171, 0.25)',
  [theme.breakpoints.down('sm')]: {
    mt: 0,
  },
});
